import type { ClientDocument } from "@/features/documents/types";
import type {
  FormCategory,
  FormFavorite,
  FormStatus,
  FormSubmission,
  GenerateParams,
  IaEstimate,
  ImportableBankAccount,
  TaxFormDef,
} from "@/features/forms/types";
import { apiFetch, apiFetchBlob } from "@/lib/api";

/** The raw ia-estimate payload (snake_case) the backend returns under `estimate`. */
interface ApiIaEstimate {
  monthly_payment: string;
  ia_type: string;
  ia_type_label: string;
  total_due: string;
  calculated_months: number;
  missing_cseds: number[];
  expired_cseds: number[];
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

// --- registry (reference.TaxForm) -------------------------------------------

interface ApiTaxForm {
  id: string;
  form_number: string;
  title: string;
  category: FormCategory;
  jurisdiction: "FEDERAL" | "STATE";
  applies_to_individuals: boolean;
  applies_to_businesses: boolean;
  is_active: boolean;
}

function toTaxForm(api: ApiTaxForm): TaxFormDef {
  return {
    id: api.id,
    formNumber: api.form_number,
    title: api.title,
    category: api.category,
    jurisdiction: api.jurisdiction,
    appliesToIndividuals: api.applies_to_individuals,
    appliesToBusinesses: api.applies_to_businesses,
    isActive: api.is_active,
  };
}

/** GET /reference/tax-forms/ — the IRS form registry. */
export async function listTaxForms(): Promise<TaxFormDef[]> {
  const body = await apiFetch<Envelope<ApiTaxForm[]>>("/reference/tax-forms/?page_size=100");
  return body.data.map(toTaxForm);
}

// --- form submissions -------------------------------------------------------

interface ApiFormSubmission {
  id: string;
  client: string;
  client_name: string;
  case: string | null;
  tax_form: string;
  form_number: string;
  form_title: string;
  data: Record<string, unknown>;
  status: FormStatus;
  status_display: string;
  generated_document: unknown | null;
  generatable: boolean;
  created_at: string;
  updated_at: string;
}

// The nested document arrives snake_case; map only the fields the UI uses.
function toDocument(raw: Record<string, unknown> | null): ClientDocument | null {
  if (!raw) return null;
  const d = raw as Record<string, string | number | null>;
  return {
    id: String(d.id),
    client: String(d.client),
    clientName: String(d.client_name ?? ""),
    case: (d.case as string | null) ?? null,
    folder: (d.folder as string | null) ?? null,
    name: String(d.name ?? ""),
    source: d.source as ClientDocument["source"],
    sourceDisplay: String(d.source_display ?? ""),
    status: d.status as ClientDocument["status"],
    statusDisplay: String(d.status_display ?? ""),
    formNumber: String(d.form_number ?? ""),
    version: Number(d.version ?? 1),
    versionGroup: String(d.version_group ?? ""),
    contentType: String(d.content_type ?? ""),
    sizeBytes: Number(d.size_bytes ?? 0),
    downloadPath: String(d.download_path ?? ""),
    isDeleted: Boolean(d.is_deleted ?? false),
    deletedAt: (d.deleted_at as string | null) ?? null,
    uploadedBy: (d.uploaded_by as string | null) ?? null,
    uploadedByName: (d.uploaded_by_name as string | null) ?? null,
    isPublished: Boolean(d.is_published ?? false),
    proformaStatus: (d.proforma_status as string | null) ?? null,
    proformaStatusDisplay: (d.proforma_status_display as string | null) ?? null,
    createdAt: String(d.created_at ?? ""),
    updatedAt: String(d.updated_at ?? ""),
  };
}

function toSubmission(api: ApiFormSubmission): FormSubmission {
  return {
    id: api.id,
    client: api.client,
    clientName: api.client_name,
    case: api.case,
    taxForm: api.tax_form,
    formNumber: api.form_number,
    formTitle: api.form_title,
    data: api.data,
    status: api.status,
    statusDisplay: api.status_display,
    generatedDocument: toDocument(api.generated_document as Record<string, unknown> | null),
    generatable: api.generatable,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/** GET /forms/?client= — a client's form submissions. */
export async function listClientFormSubmissions(
  clientId: string,
): Promise<FormSubmission[]> {
  const body = await apiFetch<Envelope<ApiFormSubmission[]>>(
    `/forms/?page_size=100&client=${clientId}`,
  );
  return body.data.map(toSubmission);
}

/** POST /forms/ */
async function createSubmission(
  clientId: string,
  taxFormId: string,
): Promise<FormSubmission> {
  const body = await apiFetch<Envelope<ApiFormSubmission>>("/forms/", {
    method: "POST",
    json: { client: clientId, tax_form: taxFormId },
  });
  return toSubmission(body.data);
}

/** POST /forms/:id/generate/ — optional Save-to-Documents params (name/folder/overwrite). */
async function generateSubmission(
  id: string,
  params?: GenerateParams,
): Promise<FormSubmission> {
  const json: Record<string, unknown> = {};
  if (params?.documentName) json.document_name = params.documentName;
  if (params?.folderId !== undefined) json.folder = params.folderId;
  if (params?.overwrite) json.overwrite = true;
  const body = await apiFetch<Envelope<ApiFormSubmission>>(`/forms/${id}/generate/`, {
    method: "POST",
    json,
  });
  return toSubmission(body.data);
}

/**
 * Start a submission for a form and immediately generate its PDF. Convenience
 * for the "Generate" button on a form that has no submission yet.
 */
export async function generateFormForClient(
  clientId: string,
  taxFormId: string,
): Promise<FormSubmission> {
  const submission = await createSubmission(clientId, taxFormId);
  return generateSubmission(submission.id);
}

/** Generate (or regenerate) an existing submission — the "Save to Documents" action. */
export function generateSubmissionPdf(
  id: string,
  params?: GenerateParams,
): Promise<FormSubmission> {
  return generateSubmission(id, params);
}

/** Regenerate an existing submission (e.g. "Regenerate" on a completed form). */
export function regenerateSubmission(id: string): Promise<FormSubmission> {
  return generateSubmission(id);
}

// --- favorites & recent -----------------------------------------------------

interface ApiFavorite {
  id: string;
  tax_form: string;
  form_number: string;
  form_title: string;
}

function toFavorite(api: ApiFavorite): FormFavorite {
  return {
    id: api.id,
    taxForm: api.tax_form,
    formNumber: api.form_number,
    formTitle: api.form_title,
  };
}

/** GET /forms/favorites/ — the current user's favorited forms. */
export async function listFavorites(): Promise<FormFavorite[]> {
  const body = await apiFetch<Envelope<ApiFavorite[]>>("/forms/favorites/");
  return body.data.map(toFavorite);
}

/** POST /forms/favorites/ — favorite a form (idempotent). */
export async function addFavorite(taxFormId: string): Promise<FormFavorite> {
  const body = await apiFetch<Envelope<ApiFavorite>>("/forms/favorites/", {
    method: "POST",
    json: { tax_form: taxFormId },
  });
  return toFavorite(body.data);
}

/** DELETE /forms/favorites/ — un-favorite a form. */
export function removeFavorite(taxFormId: string): Promise<void> {
  return apiFetch<void>("/forms/favorites/", {
    method: "DELETE",
    json: { tax_form: taxFormId },
  });
}

/** GET /forms/recent/?client= — the client's recently opened/edited forms. */
export async function listRecentSubmissions(
  clientId: string,
): Promise<FormSubmission[]> {
  const body = await apiFetch<Envelope<ApiFormSubmission[]>>(
    `/forms/recent/?client=${clientId}`,
  );
  return body.data.map(toSubmission);
}

/** GET /forms/:id/ — retrieve a submission (marks it recently-opened). */
export async function getSubmission(id: string): Promise<FormSubmission> {
  const body = await apiFetch<Envelope<ApiFormSubmission>>(`/forms/${id}/`);
  return toSubmission(body.data);
}

/**
 * Return the client's latest submission for a form, creating a fresh (prefilled)
 * one if none exists — the entry point for the fill page.
 */
export async function getOrCreateSubmission(
  clientId: string,
  taxFormId: string,
): Promise<FormSubmission> {
  const submissions = await listClientFormSubmissions(clientId);
  const existing = submissions.find((s) => s.taxForm === taxFormId);
  return existing ?? createSubmission(clientId, taxFormId);
}

// --- 433-D "import bank from 433-A" -----------------------------------------

/** GET /forms/:id/import-bank/ — the client's personal (433-A) bank accounts. */
export async function listImportableBankAccounts(
  id: string,
): Promise<ImportableBankAccount[]> {
  const body = await apiFetch<Envelope<
    { id: string; institution: string; account_type: string; masked: string }[]
  >>(`/forms/${id}/import-bank/`);
  return body.data.map((a) => ({
    id: a.id,
    institution: a.institution,
    accountType: a.account_type,
    masked: a.masked,
  }));
}

/**
 * GET /forms/:id/import-bank/?account= — the chosen account's full number +
 * institution, to prefill the 433-D bank fields (routing isn't stored, so the
 * caller clears it).
 */
export async function importBankAccount(
  id: string,
  accountId: string,
): Promise<{ accountNumber: string; institution: string }> {
  const body = await apiFetch<Envelope<{ account_number: string; institution: string }>>(
    `/forms/${id}/import-bank/?account=${encodeURIComponent(accountId)}`,
  );
  return { accountNumber: body.data.account_number, institution: body.data.institution };
}

/**
 * GET /forms/:id/ia-estimate/ — the Tier 2.2 estimated installment-agreement
 * monthly payment + IA type for the 9465 editor's read-only comparison line.
 * Returns null when the client has no financial worksheet yet.
 */
export async function getIaEstimate(id: string): Promise<IaEstimate | null> {
  const body = await apiFetch<Envelope<{ estimate: ApiIaEstimate | null }>>(
    `/forms/${id}/ia-estimate/`,
  );
  const e = body.data.estimate;
  if (!e) return null;
  return {
    monthlyPayment: e.monthly_payment,
    iaType: e.ia_type,
    iaTypeLabel: e.ia_type_label,
    totalDue: e.total_due,
    calculatedMonths: e.calculated_months,
    missingCseds: e.missing_cseds,
    expiredCseds: e.expired_cseds,
  };
}

/** PATCH /forms/:id/ — persist the edited form data (the "Save" action). */
export async function saveSubmissionData(
  id: string,
  data: Record<string, unknown>,
  status?: FormStatus,
): Promise<FormSubmission> {
  const body = await apiFetch<Envelope<ApiFormSubmission>>(`/forms/${id}/`, {
    method: "PATCH",
    json: status ? { data, status } : { data },
  });
  return toSubmission(body.data);
}

/**
 * PATCH /forms/:id/ — attach the submission to a case (without touching `data`).
 * Used by the case-scoped Spouse-Relief wizard so the one client 8857 record
 * becomes case-linked (its assets read from that case's worksheet at fill time),
 * keeping the plain editor and both wizard variants on a single submission.
 */
export async function linkSubmissionCase(
  id: string,
  caseId: string,
): Promise<FormSubmission> {
  const body = await apiFetch<Envelope<ApiFormSubmission>>(`/forms/${id}/`, {
    method: "PATCH",
    json: { case: caseId },
  });
  return toSubmission(body.data);
}

/**
 * POST /forms/:id/preview/ — fill the PDF from the submission's saved data and
 * open it inline in a new tab. Does not create a Documents record.
 *
 * Pass a `targetWindow` opened synchronously in the click handler so popup
 * blockers don't kill the tab (we can't open one after awaiting the fetch).
 */
export async function previewSubmissionPdf(
  id: string,
  targetWindow?: Window | null,
): Promise<void> {
  const blob = await apiFetchBlob(`/forms/${id}/preview/`, { method: "POST" });
  const url = URL.createObjectURL(blob);
  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = url;
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
